/**
 * Read-state derivation for the player page's Reports & Notes tab (SE-7872).
 *
 * Both cards on this tab rendered a failed read exactly like a successful one
 * that returned nothing. The notes card destructured only `data` and `refetch`
 * from its query, so `error` and `loading` were never read; the scouting
 * reports card wrote `scoutReports?.map(…) ?? []`, coercing the failed-read
 * `null` into an empty array. A scout then saw an empty list on a page that
 * otherwise looked healthy, with nothing indicating failure and no way to
 * retry — and "this player has no notes" is a conclusion a scout acts on.
 *
 * The precedence encoded below is SE-7580's, established for Similar Players
 * on this same page (`utils/similarPlayersQueryState.ts`). It is restated here
 * rather than shared because the two cards' inputs differ in kind — see each
 * function — but the ORDERING is deliberately identical, so the three sections
 * of the page answer the same question the same way.
 *
 * ## What each read can and cannot distinguish
 *
 * NOTES — the card owns `NotesListNotesByPlayerId`. The Apollo client sets
 * `errorPolicy: "all"` (`providers/ApolloClient.tsx`), so a GraphQL or network
 * failure RESOLVES with an `error` on the hook result instead of rejecting.
 * The failure was always observable; it was simply never read. (A `catch`
 * would never have run — the same trap as SE-6278 and SE-7580.)
 *
 * SCOUT REPORTS — the card owns no query. It receives `scoutReports` as a prop
 * threaded down from the page's `PlayerDetailGetPlayer` read, so there is no
 * hook result to destructure and the ticket's "read `error` in both
 * containers" does not apply literally to this half. What makes the failure
 * observable anyway is the field's own nullability: backend-v2 resolves it
 * with `@ResolveField(() => [PlayerScoutReport], { name: "scoutReports" })`
 * returning `Promise<PlayerScoutReport[]>`, so a successful read yields an
 * array and never `null`. A `null` can only be GraphQL nulling the field after
 * its resolver failed — which, under `errorPolicy: "all"`, arrives as data
 * plus an error rather than as a rejection. So `null` means failed and `[]`
 * means genuinely none, and `?? []` was erasing exactly that distinction.
 *
 * NEITHER can distinguish "withheld by entitlements" from "genuinely none" —
 * no permission discriminator is carried on either shape. That state is
 * therefore not modelled; it would be invented rather than observed.
 * @module features/player-detail/utils/reportsNotesReadState
 */

import type {
  PlayerNotesReadState,
  ScoutReportsReadState,
} from "@/features/player-detail/types";
import type { PlayerDetailScoutReportsFragment } from "@/generated/graphql";

/** The notes query's root field. */
const NOTES_ROOT_FIELD = "listNotesByPlayerId";
/** The connection's edge list, beneath the root field. */
const NOTES_EDGES_FIELD = "edges";
/**
 * The segments from `edges[N]` down to the ONE nullable field the notes
 * selection contains.
 *
 * This list is the whole safety argument, and it is a statement about
 * NULLABILITY rather than about depth. In `NotesListNotesByPlayerId`,
 * `edges`, `NoteEdge.cursor`, `NoteEdge.node` and `Note.user` are all
 * non-nullable; only `user.cognitoUser` is nullable. GraphQL null-propagates
 * an error up through non-nullable parents, so an error anywhere under a
 * non-nullable field nulls the whole connection and the server sends
 * `data: null` — however deep its path is.
 *
 * So "deep enough to be inside a note" is not the property that decides
 * whether the rows survive. `Note.user` is resolved by
 * `@ResolveField("user", () => User)` in backend-v2 with no `nullable: true`,
 * and a missing author makes its loader reject: the error arrives at
 * `[root, "edges", 0, "node", "user"]`, which is five segments deep INSIDE a
 * note and still destroys the entire list.
 *
 * Terminating at or below this path is therefore the only shape that can
 * leave the notes intact. If the selection set ever gains another nullable
 * field, errors on it are treated as read failures until it is added here —
 * which is the fail-closed direction.
 */
const PER_NOTE_NULLABLE_LEAF = ["node", "user", "cognitoUser"] as const;

/**
 * Whether one GraphQL error leaves the note list itself intact.
 *
 * True only for an error at or below `user.cognitoUser` on a single edge —
 * the throttled Cognito lookup this exemption exists for. Everything else is
 * a read failure, including shapes this function does not recognise: see
 * {@link isNotesReadError}'s fail-closed contract.
 *
 * Two things this is NOT allowed to be, both learned by shipping them:
 *
 * - A check on `path[0]`. The Cognito error's path BEGINS with the root
 *   field, so `path[0] === root` matches the very error being exempted.
 * - A check on DEPTH plus an index segment. That exempted
 *   `[root, "edges", 0, "node"]` and `[root, "edges", 0, "node", "user"]`,
 *   both non-nullable and both list-destroying — reintroducing this ticket's
 *   original defect, a failed read rendering as "No notes yet".
 *
 * The required depth now falls OUT of matching the leaf path rather than
 * being asserted separately, so there is no second number to keep in step
 * with the selection set.
 * @param path - The GraphQL error's `path`, as the server reported it.
 * @returns True only for an error at or below the one nullable leaf.
 */
export const isPerNoteFieldError = (
  path: readonly (string | number)[] | undefined
): boolean =>
  Array.isArray(path) &&
  path[0] === NOTES_ROOT_FIELD &&
  path[1] === NOTES_EDGES_FIELD &&
  typeof path[2] === "number" &&
  PER_NOTE_NULLABLE_LEAF.every((segment, index) => path[3 + index] === segment);

/** The subset of `ApolloError` this module needs to classify a read. */
export interface NotesReadErrorLike {
  /** Transport failure, if any. */
  readonly networkError?: unknown;
  /** Field-level failures the server reported alongside the data. */
  readonly graphQLErrors?: readonly {
    readonly path?: readonly (string | number)[];
  }[];
}

/**
 * Whether the hook's error is about READING THE NOTES, as opposed to a field
 * hanging off a note that arrived fine.
 *
 * ## Why this cannot be answered by looking at the data
 *
 * The obvious test — "did the rows survive?" — is wrong, and wrong in the
 * dangerous direction. Verified against the installed Apollo 3.14.1:
 *
 * - `listNotesByPlayerId` is NON-NULLABLE in the schema, so when its resolver
 *   throws, GraphQL null-propagates to the root and the server sends
 *   `data: null`.
 * - `core/QueryInfo.js` `shouldWriteResult()` writes an errored result to the
 *   cache only when `result.data` is truthy — even under `errorPolicy: "all"`.
 *   So nothing is written.
 * - `core/ObservableQuery.js` then sets `result.data = diff.result`, serving
 *   the untouched cache.
 *
 * The hook therefore hands back the PREVIOUS rows with an error set and
 * `networkError === null`. Rows surviving is the NORMAL state after a failed
 * refetch, not evidence that the refresh succeeded. Keying on it meant that
 * after the first successful load no server-side failure could ever surface
 * again: a scout whose session expired pressed Retry and watched the staleness
 * banner disappear over notes of arbitrary age.
 *
 * ## Fail-closed
 *
 * So the default is inverted. An error is a read error UNLESS every reported
 * GraphQL error is recognisably inside one note's subtree. An unrecognised
 * shape — no `graphQLErrors` at all, a path this module cannot parse, a mix —
 * counts as a read failure. Disclosing staleness that turns out to be fresh
 * costs a banner; hiding a failure costs the scout a false fact about a
 * player, which is the whole subject of this ticket.
 * @param error - The hook's error, or undefined when the read succeeded.
 * @returns Whether the card should treat this as a failed or stale read.
 */
export const isNotesReadError = (
  error: NotesReadErrorLike | undefined | null
): boolean => {
  if (!error) {
    return false;
  }
  if (error.networkError) {
    return true;
  }
  const graphQLErrors = error.graphQLErrors ?? [];
  if (graphQLErrors.length === 0) {
    // An error object carrying neither a transport failure nor a reported
    // GraphQL error is a shape this module does not understand. Fail closed.
    return true;
  }
  return !graphQLErrors.every(one => isPerNoteFieldError(one.path));
};

/** Inputs to {@link deriveNotesReadState}. */
interface NotesReadStateInput {
  /** How many notes the read produced (zero is not evidence of none). */
  readonly noteCount: number;
  /**
   * Whether an answer is still outstanding. TRUE while the query is in flight
   * AND while it is skipped for want of a `playerId` — in both cases an absent
   * result is not evidence that the player has no notes.
   */
  readonly isPending: boolean;
  /** Whether the hook surfaced a GraphQL or network error. */
  readonly hasError: boolean;
}

/**
 * Maps a notes read onto the state its card should render.
 *
 * Precedence, and why (SE-7580's ordering, unchanged):
 * 1. Notes in hand win — a scout with notes on screen is shown those notes.
 *    An error alongside them downgrades `ready` to `stale`, so a failed
 *    refresh is disclosed rather than swallowed.
 * 2. In-flight beats a stale error, so retrying shows progress instead of
 *    pinning the old failure on screen.
 * 3. An error with nothing to show is `failed` — never `empty`.
 * 4. Only a settled, error-free, empty read may claim `empty`.
 * @param input - Note count, pending flag, and error flag.
 * @param input.noteCount - How many notes the read produced.
 * @param input.isPending - Whether an answer is still outstanding.
 * @param input.hasError - Whether the hook surfaced an error.
 * @returns The single state the notes card should render.
 */
export const deriveNotesReadState = ({
  noteCount,
  isPending,
  hasError,
}: NotesReadStateInput): PlayerNotesReadState => {
  if (noteCount > 0) {
    return hasError ? "stale" : "ready";
  }
  if (isPending) {
    return "loading";
  }
  if (hasError) {
    return "failed";
  }
  return "empty";
};

/**
 * Maps the `scoutReports` prop onto the state its card should render.
 *
 * The three input shapes are distinct on the wire and must stay distinct on
 * screen:
 * - `undefined` — the player itself has not resolved yet. Both fragments that
 *   feed this card (`PlayerDetailPlayer`, `PlayerDetailPlayerMeta`) select
 *   `scoutReports`, so the field is absent only when the player is.
 * - `null` — the field's resolver failed and GraphQL nulled it. Never a
 *   successful answer; see the module comment.
 * - `[]` — the read succeeded and this player genuinely has no scout reports.
 *   This is common and legitimate, and keeps the card's existing empty copy.
 * @param scoutReports - The scout reports prop, exactly as received.
 * @returns The single state the scouting reports card should render.
 */
export const deriveScoutReportsReadState = (
  scoutReports: readonly PlayerDetailScoutReportsFragment[] | null | undefined
): ScoutReportsReadState => {
  if (scoutReports === undefined) {
    return "loading";
  }
  if (scoutReports === null) {
    return "failed";
  }
  return scoutReports.length > 0 ? "ready" : "empty";
};
